import { Page } from "../../../models/cores/Page.model";
import axiosCustom from "../../../utils/axiosInstance";
import { ShipmentTruckingFilterDto } from "../dtos/request/ShipmentTruckingFilter.dto";
import { TruckingExecuteActionRequestDto } from "../dtos/request/TruckingExecuteActionRequest.dto";
import { TruckingShipmentAssignDto } from "../dtos/request/TruckingShipmentAssign.dto";
import { ShipmentTruckingListResponseDto } from "../dtos/response/ShipmentTruckingListResponse.dto";
import { TruckingExecuteActionResponseDto } from "../dtos/response/TruckingExecuteActionResponse.dto";
import { ShipmentTruckingStatusCountResponseDto } from "../dtos/response/ShipmentTruckingStatusCountResponseDto";
import { TruckingFileResponseDto, TruckingFileType } from "../dtos/response/TruckingFileResponseDto";
import { downloadBlob, xlsxFilename } from "../../../utils/downloadBlob";
import { ShipmentTruckingExportRequestDto } from "../dtos/request/ShipmentTruckingExportRequestDto";
import { ShipmentTruckingOperationalOutcomeRequestDto } from "../dtos/request/ShipmentTruckingOperationalOutcomeRequestDto";
import { ShipmentTruckingLotReopenRequestDto } from "../dtos/request/ShipmentTruckingLotReopenRequestDto";
import { ShipmentHistoryResponseDto } from "../dtos/response/ShipmentHistoryResponseDto";

const API_BASE_URL = "/shipment-truckings";

export interface RefDropdownParams {
  tableName: string;
  displayColumn: string;
  valueColumn: string;
  extraColumns?: string;
  refCondition?: string;
  [key: string]: string | undefined; // For [_this] values
}

export interface RefDropdownOption {
  key: string;
  value: string;
  [extraColumn: string]: any;
}

export interface ShipmentTruckingRoutePointRequestDto {
  stopOrder: number;
  stopTypeId: number;
  locationId: string;
  id?: string;
}

export interface ShipmentTruckingRoutePointResponseDto {
  id: string;
  stopOrder: number;
  stopTypeId: number;
  locationId: string;
}

export const shipmentTruckingService = {
  executeAction: async (truckingId: string, request: TruckingExecuteActionRequestDto): Promise<TruckingExecuteActionResponseDto> => {
    const res = await axiosCustom.post<TruckingExecuteActionResponseDto>(`${API_BASE_URL}/${truckingId}/execute-action`, request);
    return res.data;
  },

  recordOperationalOutcome: async (
    truckingId: string,
    request: ShipmentTruckingOperationalOutcomeRequestDto,
  ): Promise<void> => {
    await axiosCustom.post(`${API_BASE_URL}/${truckingId}/operational-outcomes`, request);
  },

  reopenLotOutcome: async (
    truckingId: string,
    request: ShipmentTruckingLotReopenRequestDto,
  ): Promise<void> => {
    await axiosCustom.post(`${API_BASE_URL}/${truckingId}/operational-outcomes/reopen`, request);
  },

  getRefDropdownOptions: async (params: RefDropdownParams) => axiosCustom.get<RefDropdownOption[]>(`/master-trucking/ref-dropdown-list`, { params }),

  deleteSharedTrucking: async (truckingId: string, shipmentId: string) =>
    axiosCustom.delete<void>(`${API_BASE_URL}/${truckingId}/shipments/${shipmentId}`),

  removeJobFromTrucking: async (truckingId: string, shipmentId: string): Promise<void> => {
    await axiosCustom.delete(`${API_BASE_URL}/${truckingId}/jobs/${shipmentId}`);
  },

  deleteTrucking: async (truckingId: string): Promise<void> => {
    await axiosCustom.delete<Promise<void>>(`${API_BASE_URL}/${truckingId}`);
  },

  assignShipmentShareTrucking: async (dto: TruckingShipmentAssignDto): Promise<void> => {
    await axiosCustom.post<void>(`${API_BASE_URL}/assign-shipments`, dto);
  },

  saveRoutePoints: async (truckingId: string, routePoints: ShipmentTruckingRoutePointRequestDto[]): Promise<void> => {
    await axiosCustom.put(`${API_BASE_URL}/${truckingId}/route-points`, routePoints);
  },

  getRoutePointsByTruckingId: async (truckingId: string): Promise<ShipmentTruckingRoutePointResponseDto[]> => {
    const res = await axiosCustom.get<ShipmentTruckingRoutePointResponseDto[]>(`${API_BASE_URL}/${truckingId}/route-points`);
    return res.data;
  },

  cutMooc: async (truckingId: string) => {
    const res = await axiosCustom.post(`${API_BASE_URL}/${truckingId}/cut-mooc`);
    return res.data;
  },

  getStatusCount: async (): Promise<ShipmentTruckingStatusCountResponseDto> => {
    const res = await axiosCustom.get<ShipmentTruckingStatusCountResponseDto>(`${API_BASE_URL}/status-count`);
    return res.data;
  },

  getDetail: async (truckingId: string): Promise<ShipmentTruckingListResponseDto> => {
    const res = await axiosCustom.get<ShipmentTruckingListResponseDto>(`${API_BASE_URL}/${truckingId}/detail`);
    return res.data;
  },

  getHistory: async (truckingId: string): Promise<ShipmentHistoryResponseDto[]> => {
    const res = await axiosCustom.get<ShipmentHistoryResponseDto[]>(`${API_BASE_URL}/${truckingId}/history`);
    return res.data;
  },

  search: async (keyWord: string, filter: ShipmentTruckingFilterDto, page = 0, size = 50): Promise<Page<ShipmentTruckingListResponseDto>> => {
    const res = await axiosCustom.post(`${API_BASE_URL}/search`, filter, {
      params: {
        keyWord,
        page,
        size,
      },
    });
    return res.data;
  },

  exportExcelByShipmentId: async (shipmentId: string): Promise<void> => {
    const res = await axiosCustom.get(`/shipments/${shipmentId}/export-trucking`, {
      responseType: "blob",
    });
    downloadBlob(res.data, xlsxFilename("THONG_TIN_VAN_CHUYEN"));
  },

  exportExcel: async (keyWord: string, request: ShipmentTruckingExportRequestDto): Promise<void> => {
    const res = await axiosCustom.post(`${API_BASE_URL}/export`, request, {
      params: { keyWord },
      responseType: "blob",
    });
    downloadBlob(res.data, xlsxFilename("THONG_TIN_VAN_CHUYEN"));
  },

  getFiles: async (truckingId: string, fileType?: TruckingFileType): Promise<TruckingFileResponseDto[]> => {
    const res = await axiosCustom.get<TruckingFileResponseDto[]>(`${API_BASE_URL}/${truckingId}/files`, {
      params: fileType ? { fileType } : undefined,
    });
    return res.data;
  },

  getFilesByShipment: async (shipmentId: string): Promise<TruckingFileResponseDto[]> => {
    const res = await axiosCustom.get<TruckingFileResponseDto[]>(`${API_BASE_URL}/shipment/${shipmentId}/files`);
    return res.data;
  },

  getTruckingOptionsByShipment: async (shipmentId: string): Promise<{ key: string; value: string }[]> => {
    const res = await axiosCustom.get<{ key: string; value: string }[]>(`${API_BASE_URL}/shipment/${shipmentId}/options`);
    return res.data;
  },

  uploadFiles: async (
    truckingId: string,
    files: File[],
    fileType?: TruckingFileType,
    shipmentIds?: string[],
  ): Promise<TruckingFileResponseDto[]> => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    const res = await axiosCustom.post<TruckingFileResponseDto[]>(`${API_BASE_URL}/${truckingId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      params: {
        ...(fileType ? { fileType } : {}),
        ...(shipmentIds?.length ? { shipmentIds: shipmentIds.join(",") } : {}),
      },
    });
    return res.data;
  },

  deleteFile: async (truckingId: string, fileId: string): Promise<void> => {
    await axiosCustom.delete(`${API_BASE_URL}/${truckingId}/files/${fileId}`);
  },

  unlinkFileFromShipment: async (shipmentId: string, fileId: string): Promise<void> => {
    await axiosCustom.delete(`${API_BASE_URL}/shipment/${shipmentId}/files/${fileId}`);
  },

  downloadFile: async (truckingId: string, fileId: string): Promise<ArrayBuffer> => {
    const res = await axiosCustom.get<ArrayBuffer>(`${API_BASE_URL}/${truckingId}/files/${fileId}/download`, {
      responseType: "arraybuffer",
    });
    return res.data;
  },

  downloadFileForShipment: async (shipmentId: string, fileId: string): Promise<ArrayBuffer> => {
    const res = await axiosCustom.get<ArrayBuffer>(`${API_BASE_URL}/shipment/${shipmentId}/files/${fileId}/download`, {
      responseType: "arraybuffer",
    });
    return res.data;
  },

  downloadAllFilesForShipment: async (shipmentId: string, truckingId?: string): Promise<ArrayBuffer> => {
    const res = await axiosCustom.get<ArrayBuffer>(`${API_BASE_URL}/shipment/${shipmentId}/files/download-all`, {
      params: truckingId ? { truckingId } : undefined,
      responseType: "arraybuffer",
    });
    return res.data;
  },
};
